import { useLingui } from "@lingui/react/macro";
import { useParams } from "react-router";
import { startOfToday, addDays } from "date-fns";
import { z } from "zod";
import * as Sentry from "@sentry/react";
import NoResults from "@/components/NoResults/NoResults";
import { Loading } from "@medoma/components/Loading/Loading";
import ErrorMessage from "@medoma/components/ErrorMessage/ErrorMessage";
import { deducedError } from "@/Utils/ErrorUtils";
import { usePatientsActivityOccurrencesAndGroups } from "@/api/Activities";
import { knownFeatureFlagsSchema, useFeatureFlag } from "@/api/FeatureFlags";
import { AddActivityAction } from "./AddActivityAction";
import { ActivitiesList } from "./ActivitiesList";
import styles from "./ActivitiesPage.module.scss";

export const ActivitiesPage = () => {
  const { patientId } = z.object({ patientId: z.string() }).parse(useParams());
  const { t } = useLingui();

  const today = startOfToday();
  const startDate = today;
  const endDate = addDays(today, 14);

  const {
    data: activityOccurrencesAndGroups = [],
    isPending,
    isError,
    error,
  } = usePatientsActivityOccurrencesAndGroups(patientId, startDate, endDate);
  const { data: isActivityFunctionalityInGoEnabled } = useFeatureFlag(
    knownFeatureFlagsSchema.enum.FE_ActivityFunctionalityInGo,
  );

  if (isPending) {
    return <Loading message={t`Hämtar aktiviteter`} />;
  }

  if (isError) {
    Sentry.captureException(error);
    return (
      <ErrorMessage
        message={`${t`Kunde inte hämta aktiviteter`} ${deducedError(error)}`}
      />
    );
  }

  return (
    <div className={styles.wrapper}>
      {isActivityFunctionalityInGoEnabled ? <AddActivityAction /> : null}
      {activityOccurrencesAndGroups.length === 0 ? (
        <NoResults message={t`Inga aktiviteter hittades`} />
      ) : (
        <ActivitiesList
          activityOccurrencesAndGroups={activityOccurrencesAndGroups}
        />
      )}
    </div>
  );
};
